// Página full-screen para editar un video del HISTORIAL de redes-sociales con
// IA (Gemini Omni Flash). Feature NUEVA e independiente del editor de Sora
// (`EditarVideo`/`editar_vid`): NO lo reemplaza. Espeja el comportamiento de
// "Editar imagen" del historial (navegación a página completa) pero para videos
// ≤10s, reutilizando el componente `EditarVideoIA`. Ver ADR-500 / ADR-502.
//
// Divergencia con la timeline: el resultado de Gemini vuelve en el bucket
// `video-project-assets`; acá lo copiamos a `generadas` + creamos una fila
// NUEVA en `imagenes_generadas`. En el historial solo hay "Guardar" (item nuevo)
// o "Cancelar": NUNCA se reemplaza ni borra el video original (por eso pasamos
// `singleSave` a EditarVideoIA).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSidebar } from '../context/SidebarContext';
import {
  EditarVideoIA,
  type EditarVideoIASaveResult,
} from '../components/EditarVideoIA';
import type { OmniAspectRatio } from '../lib/omniEdit';

const GENERADAS_BUCKET = 'generadas';
const HISTORY_ROUTE = '/redes-sociales/imagenes';
// Límite del modelo Gemini Omni (segundos). Mismo umbral que el gate del
// historial (GeneratedImagesHistory) y de la timeline (VideoEditor): ≤10s.
const OMNI_MAX_DURATION = 10;

function formatoToAspect(formato?: string | null): OmniAspectRatio {
  switch ((formato || '').toLowerCase()) {
    case 'square':
      return '1:1';
    case 'landscape':
      return '16:9';
    case 'portrait':
      return '9:16';
    default:
      return '9:16';
  }
}

/**
 * Extrae el primer frame de un video (por su object-URL local) como PNG, para
 * usarlo de poster/miniatura en el historial. Best-effort: devuelve null si
 * falla o tarda demasiado (el guardado no depende de esto). Usa un blob URL
 * local → el canvas no queda "tainted" (sin problemas de CORS).
 */
function extractPosterPng(src: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    let settled = false;
    const finish = (b: Blob | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      resolve(b);
    };
    const timer = window.setTimeout(() => finish(null), 8000);
    const drawFrame = () => {
      try {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (!vw || !vh) return finish(null);
        // Miniatura: `opt/` es para thumbnails chicos → reducimos a máx 480px de
        // ancho (mantiene aspecto). Baja mucho el peso del PNG → subida casi
        // instantánea.
        const maxW = 480;
        const scale = vw > maxW ? maxW / vw : 1;
        const w = Math.round(vw * scale);
        const h = Math.round(vh * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((b) => finish(b), 'image/png');
      } catch {
        finish(null);
      }
    };
    video.onseeked = drawFrame;
    video.onloadeddata = () => {
      // Buscamos un frame apenas adentro (evita frames negros del arranque).
      try {
        video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
      } catch {
        drawFrame();
      }
    };
    video.onerror = () => finish(null);
    video.src = src;
  });
}

export const EditarVideoIAHistorial: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const imageId = (params.get('id') || '').trim();
  const companyId = (params.get('companyId') || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clipUrl, setClipUrl] = useState('');
  const [clipPath, setClipPath] = useState('');
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [aspectRatio, setAspectRatio] = useState<OmniAspectRatio>('9:16');
  // Metadatos de la fila original que copiamos a la fila nueva al guardar.
  const originalMeta = useRef<{
    varianteId: string | null;
    formato: string | null;
  }>({ varianteId: null, formato: null });

  const goBack = () => navigate(HISTORY_ROUTE);

  // Botón "atrás" nativo de mobile → volver al historial.
  useEffect(() => {
    setMobileBackOverride(() => () => goBack());
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar la fila + firmar la URL del video original.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!imageId || !companyId) {
        setError('Faltan datos del video para editarlo.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        // Resolver la fila: por id (UUID), fallback file_name exacto o prefijo.
        const sel = 'id, file_name, formato, variante_id, tipo';
        let row:
          | {
              id: string;
              file_name: string;
              formato: string | null;
              variante_id: string | null;
              tipo: string | null;
            }
          | null = null;
        const { data: byId } = await supabase
          .from('imagenes_generadas')
          .select(sel)
          .eq('id', imageId)
          .eq('id_company', companyId)
          .maybeSingle();
        if (byId) row = byId as any;
        if (!row) {
          const { data: byName } = await supabase
            .from('imagenes_generadas')
            .select(sel)
            .eq('file_name', imageId)
            .eq('id_company', companyId)
            .maybeSingle();
          if (byName) row = byName as any;
        }
        if (!row) {
          const { data: byLike } = await supabase
            .from('imagenes_generadas')
            .select(sel)
            .like('file_name', `${imageId}.%`)
            .eq('id_company', companyId)
            .maybeSingle();
          if (byLike) row = byLike as any;
        }
        if (!row?.file_name) throw new Error('El video no está disponible.');

        const path = `${companyId}/full/${row.file_name}`;
        const { data: signed } = await supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(path, 3600);
        if (!signed?.signedUrl)
          throw new Error('No se pudo obtener el video para editar.');

        originalMeta.current = {
          varianteId: row.variante_id ?? null,
          formato: row.formato ?? null,
        };
        if (!cancelled) {
          setClipPath(path);
          setClipUrl(signed.signedUrl);
          setAspectRatio(formatoToAspect(row.formato));
        }
      } catch (e: any) {
        if (!cancelled)
          setError(e?.message || 'Error al cargar el video para editar.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [imageId, companyId]);

  // Guardado: baja el mp4 editado (video-project-assets), lo sube a `generadas`
  // y crea una fila NUEVA en `imagenes_generadas`. Nunca reemplaza ni borra el
  // video original. Lanza en caso de error → EditarVideoIA lo muestra y NO cierra.
  const handleSave = async (result: EditarVideoIASaveResult) => {
    // 1) bajar el mp4 editado por su signed URL.
    const resp = await fetch(result.url);
    if (!resp.ok) throw new Error('No se pudo descargar el video editado.');
    const blob = await resp.blob();

    // 2) subir a `generadas` con un nombre único.
    const newFileName = `${crypto.randomUUID()}.mp4`;
    const newPath = `${companyId}/full/${newFileName}`;
    const { error: upErr } = await supabase.storage
      .from(GENERADAS_BUCKET)
      .upload(newPath, blob, { contentType: 'video/mp4', upsert: false });
    if (upErr) throw new Error('No se pudo guardar el video en el historial.');

    // 2b) generar un poster (primer frame) y subirlo a `opt/{nombre}.png` para
    // que el historial muestre miniatura en vez del placeholder gris. El grid
    // busca la miniatura del video justo en esa ruta. Best-effort: si falla, el
    // video igual queda guardado (con placeholder).
    try {
      const blobUrl = URL.createObjectURL(blob);
      const poster = await extractPosterPng(blobUrl);
      URL.revokeObjectURL(blobUrl);
      if (poster) {
        const posterName = newFileName.replace(/\.mp4$/i, '.png');
        await supabase.storage
          .from(GENERADAS_BUCKET)
          .upload(`${companyId}/opt/${posterName}`, poster, {
            contentType: 'image/png',
            upsert: false,
          });
      }
    } catch {
      // El poster es un extra: si falla, no bloquea el guardado.
    }

    // 3) insertar la fila (id/created_at los pone la DB por default).
    const { formato, varianteId } = originalMeta.current;
    const { error: insErr } = await supabase.from('imagenes_generadas').insert({
      id_company: companyId,
      file_name: newFileName,
      tipo: 'video',
      formato,
      variante_id: varianteId,
      active: true,
    });
    if (insErr) throw new Error('No se pudo registrar el video editado.');
    // El original queda intacto (no se desactiva ni borra): el editado se suma
    // como item NUEVO. Al resolver, EditarVideoIA llama onClose → historial.
  };

  const tooLong = durationSec !== null && durationSec > OMNI_MAX_DURATION;
  const ready = !!clipUrl && durationSec !== null && !tooLong;

  return (
    <div
      className="w-full bg-surface-content min-h-[100dvh]"
      style={{ ['--sb' as any]: `${sidebarWidth}px` }}
    >
      {/* Header (Cancelar/Atrás + título centrado) — SOLO desktop. En mobile ya
          está el botón flotante "volver" global del Sidebar (mobileBackOverride),
          así que esta barra sería redundante. Mismo criterio que "Editar imagen". */}
      <div className="hidden md:block fixed top-0 right-0 left-0 md:left-[var(--sb)] z-40 bg-white border-b border-black/[0.06]">
        <div className="flex items-center justify-between h-14 px-4 md:px-8 max-w-content mx-auto">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Atrás</span>
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[15px] md:text-[17px] font-semibold text-brand-black/90">
            Editar video con IA
          </h1>
          <button
            type="button"
            onClick={goBack}
            className="text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
          >
            Cancelar
          </button>
        </div>
      </div>

      {/* Contenido. En mobile arranca en top-0 con `pt-14` para despejar el
          botón flotante del Sidebar; en desktop va bajo el header propio. */}
      <div className="fixed top-0 md:top-14 bottom-0 right-0 left-0 md:left-[var(--sb)] z-30 bg-surface-content overflow-hidden pt-14 md:pt-0">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[15px] text-neutral-700">{error}</p>
            <button
              type="button"
              onClick={goBack}
              className="text-[#1644EB] text-[15px] font-medium"
            >
              Volver al historial
            </button>
          </div>
        ) : tooLong ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-[15px] text-neutral-700">
              Este video es muy largo para editarlo con IA (máx. 10 segundos).
            </p>
            <button
              type="button"
              onClick={goBack}
              className="text-[#1644EB] text-[15px] font-medium"
            >
              Volver al historial
            </button>
          </div>
        ) : ready ? (
          <div className="h-full max-w-content mx-auto w-full">
            <EditarVideoIA
              clipUrl={clipUrl}
              originalPath={clipPath}
              durationSec={durationSec as number}
              aspectRatio={aspectRatio}
              // `project_id` en omni_edit_jobs es UUID → usamos el id (UUID) del
              // video del historial (no hay "proyecto" acá). Solo se usa como
              // segmento del storage path scratch de video-project-assets.
              projectId={imageId}
              companyId={companyId}
              onSave={handleSave}
              onClose={goBack}
              showOriginalPreview
              singleSave
              // Layout horizontal (video izq + composer der), como "Editar imagen".
              layout="page"
            />
          </div>
        ) : (
          // Firmado ok pero todavía leyendo la duración del <video>.
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        )}
      </div>

      {/* Video oculto para leer la duración antes de montar el editor (el
          historial no la conoce). */}
      {!!clipUrl && durationSec === null && (
        <video
          src={clipUrl}
          preload="metadata"
          muted
          playsInline
          className="hidden"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            setDurationSec(Number.isFinite(d) && d > 0 ? d : 0);
          }}
          onError={() => setDurationSec(0)}
        />
      )}
    </div>
  );
};

export default EditarVideoIAHistorial;
